/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/

import { IModelConnection, ScreenViewport } from "@itwin/core-frontend";
import { cQuery } from "./Query";
import { cSelection } from "./Selection"
import { cMessage } from "./Message";
import { cView } from "./View";
import { cMultiViewport } from "./MultiViewport";

export class cCrossProbing {
  private static iModel: IModelConnection;
  public static sViewPort: ScreenViewport;

  public static async initCrossProbing(iModel: IModelConnection, vp: ScreenViewport) {
    this.iModel = iModel;
    this.sViewPort = vp;
  }

  public static setupCrossProbing(flag : boolean) {
    cSelection.setOnElementSelected("xProbing")
    if (flag) {
      cSelection.setOnElementSelected("xProbing", cCrossProbing.elementSelected)
    }
  }

  // this method is called when an element is selected on a viewport.
  private static elementSelected = async (sourceElementId: string[]) => {

    const eleLabel = sourceElementId[0];
    if (eleLabel) {
      const sourceVp = cView.getCurrentView();

    // if source is 3D, look for any target 2D elements.
      if (sourceVp?.view.is3d()) {
        await cCrossProbing.find2DElement(eleLabel);
        // hilite element in all viewports/midels
        const targetElement = await cQuery.getElementIdFromTag(eleLabel);
        if (targetElement) {
          cSelection.setHilite(targetElement, true);
        }
      }
    
    // if source VP is 2D, look for any target 3D elements.
      if (sourceVp?.view.is2d()) {
        await cCrossProbing.find3DElement(eleLabel);
      }
    }

    return sourceElementId;
  }

  private static async find2DElement(eleLabel : string) {

    const targetElement = await cQuery.get2dElementIdFromTag(eleLabel);
    if (targetElement) {
      const vp2d = cMultiViewport.getViewportById("Viewport2d");
      if (vp2d) {
        await cView.zoomToElement(targetElement, vp2d, 0.2);
        cSelection.setHilite(targetElement, true);
      }
    } else {
      cMessage.showStatusBarError(`${eleLabel} doesn't have a linked element`);
    }
  }

  private static async find3DElement(eleLabel : string) {

    const targetElement = await cQuery.getElementIdFromTag(eleLabel);
    if (targetElement) {
      const vp3d = cMultiViewport.getRealityModelViewport();
      if (vp3d) {
        await cView.zoomToElement(targetElement, vp3d);
        cSelection.setHilite(targetElement, true);
      }
    } else {
      cMessage.showStatusBarError(`${eleLabel} doesn't have a linked element`);
    }
  }

}
